// frontend/src/auth/handleLogout.ts
/**
 * Logout coordenado — chama backend, revoga broker session, derruba sessao
 * de cada app cross-domain via popup top-level navigation (com helper page
 * pra suportar POST), redirect final.
 *
 * Spec: docs/superpowers/specs/2026-05-06-ztna-cross-domain-sso-bridge-design.md
 */

interface AppLogout {
  slug?: string
  url: string
  method: 'GET' | 'POST'
}

interface LogoutResponse {
  status?: string
  next?: string
  iframe_hostnames?: string[]
  iframe_logout_urls?: string[]
  app_logouts?: AppLogout[]
}

const APP_LOGOUT_NAV_MS = 1500
const FINAL_REDIRECT_GRACE_MS = 300

// `inatividade`: o broker encerra a sessão do navegador mas mantém o grant do
// MCP preso a ela (consoleai#25187). Sem motivo é logout explícito, que derruba.
export type MotivoDoLogout = 'inatividade'

export async function handleLogout(opts?: { redirectTo?: string; motivo?: MotivoDoLogout }): Promise<void> {
  // Limpa Bearer token do localStorage ANTES do POST /logout.
  try {
    localStorage.removeItem('session_token')
  } catch {
    // ignore
  }

  // Limpa o store local de busca de mensagens (texto já descriptografado no
  // IndexedDB) — em dispositivo compartilhado o plaintext não pode sobreviver
  // ao logout. Best-effort (dynamic import + catch); NUNCA bloqueia o logout.
  void import('@/lib/messageStore')
    .then((m) => m.messageStore.clear())
    .catch(() => {})

  // Abre popup ANTES do await — popup blockers exigem window.open dentro
  // do task de user activation (logout click).
  const coordinator = window.open(
    'about:blank',
    '_logout_coordinator',
    'width=320,height=240,left=99999,top=99999,noopener=no',
  )

  let data: LogoutResponse = {}
  try {
    const resp = await fetch('/api/v1/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Accept': 'application/json' },
    })
    data = await resp.json()
  } catch {
    // Soft-fail
  }

  // Revoga broker session ANTES do redirect final (NOSSO dominio, sem 3rd
  // party block).
  if (data.next) {
    const fim = opts?.motivo
      ? `${data.next}${data.next.includes('?') ? '&' : '?'}motivo=${opts.motivo}`
      : data.next
    try {
      await fetch(fim, {
        method: 'GET',
        credentials: 'include',
        mode: 'no-cors',
      })
    } catch {
      // Soft-fail
    }
  }

  // Lista normalizada de apps pra derrubar — prefere app_logouts (com method)
  // sobre iframe_logout_urls (legacy GET-only).
  let logouts: AppLogout[] = []
  if (data.app_logouts && data.app_logouts.length > 0) {
    logouts = data.app_logouts
  } else if (data.iframe_logout_urls && data.iframe_logout_urls.length > 0) {
    logouts = data.iframe_logout_urls.map((url) => ({ url, method: 'GET' as const }))
  } else if (data.iframe_hostnames && data.iframe_hostnames.length > 0) {
    logouts = data.iframe_hostnames.map((h) => ({
      url: `https://${h}/_broker/auth/clear-cookie`,
      method: 'GET' as const,
    }))
  }

  // Logout cross-domain via popup top-level navigation (substituiu fetch
  // no-cors em 2026-05-07).
  //
  // Por que popup, e nao fetch:
  // Chrome (Privacy Sandbox 2024+) e Safari (ITP) bloqueiam cookies em fetch
  // cross-site mesmo com SameSite=None;Secure quando o contexto eh "third
  // party". Top-level navigation NAO eh tratada como third-party — cada
  // navegacao da popup eh first-party context pra aquele dominio.
  //
  // Por que helper page (em vez de popup.location.href direto):
  // GitLab tem `config.sign_out_via = :post` em devise — GET /users/sign_out
  // ou /sign_out NAO desloga user autenticado (302 redirect / 404). So POST
  // /users/sign_out funciona. window.location.href so faz GET.
  // Helper page (/logout-helper.html, same-origin) recebe url+method via
  // query params e faz form.submit() ou location.replace conforme method.
  // Form submit eh top-level navigation tambem — cookies enviados, Set-Cookie
  // processado.
  if (logouts.length > 0 && coordinator && !coordinator.closed) {
    for (const { url, method } of logouts) {
      const helperUrl = `${window.location.origin}/logout-helper.html`
        + `?to=${encodeURIComponent(url)}&method=${method}`
      try {
        // Cross-origin write a location eh permitido (fica same-origin
        // de novo na proxima navegacao pro helper).
        coordinator.location.href = helperUrl
      } catch {
        // ignore
      }
      await new Promise((r) => setTimeout(r, APP_LOGOUT_NAV_MS))
    }
    // Apos a ultima navegacao, popup esta em cross-origin (ex: git.lab).
    // coordinator.close() do main page pode ser bloqueado nesse contexto.
    // Navega popup de volta pro helper same-origin com close=1 — helper
    // chama window.close() de dentro do proprio popup (script-opened →
    // browser permite self-close).
    try {
      coordinator.location.href = `${window.location.origin}`
        + `/logout-helper.html?close=1`
    } catch {
      // ignore
    }
    await new Promise((r) => setTimeout(r, 300))
    // Belt-and-suspenders: tenta close do main side tambem.
    try {
      coordinator.close()
    } catch {
      // ignore
    }
  } else if (logouts.length > 0) {
    // Fallback popup blocked: tenta fetch no-cors (provavelmente nao
    // efetiva session-end cross-site, mas best-effort).
    await Promise.allSettled(
      logouts.map(({ url, method }) =>
        fetch(url, {
          method,
          credentials: 'include',
          mode: 'no-cors',
          cache: 'no-store',
        }).catch(() => {}),
      ),
    )
  } else if (coordinator && !coordinator.closed) {
    try {
      coordinator.close()
    } catch {
      // ignore
    }
  }

  setTimeout(() => {
    // Logout voluntário (botão "Sair") → /login (auto-dispara OIDC, como antes).
    // Logout involuntário (inatividade) passa redirectTo='/session-expired' p/
    // cair na tela estática em vez de reiniciar o login sozinho.
    window.location.href = opts?.redirectTo ?? '/login'
  }, FINAL_REDIRECT_GRACE_MS)
}
